import GithubSlugger from "github-slugger";
import type { TocSection } from "../../components/TableOfContents";

export interface TocEntry {
  /** Heading depth (2 = `##`, 3 = `###`). */
  depth: number;
  /** Display text with inline Markdown stripped. */
  text: string;
  /** Slug id, matching the id `rehype-slug` puts on the rendered heading. */
  id: string;
  /** Line index (0-based) of the heading in the source passed to `extractToc`. */
  line: number;
}

// Strip the inline Markdown that appears in headings so the ToC label reads as
// plain text and the slug matches what rehype-slug derives from the heading's
// text content: code backticks, bold/italic markers, and link syntax.
export function stripInline(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // [label](url) → label
    .replace(/`/g, "")
    .replace(/\*\*/g, "")
    .replace(/(^|[^*])\*(?!\*)/g, "$1")
    .trim();
}

/**
 * Extract the h2/h3 headings from a Markdown doc for the "On this page" nav.
 *
 * Every heading (h1–h6) is fed to a single GithubSlugger in document order so
 * the duplicate-suffix counter (`check-access`, `check-access-1`, …) stays in
 * lockstep with `rehype-slug`, which does the same over the rendered tree. Only
 * h2/h3 are returned for display; the h1 is the page title, shown in the header.
 *
 * Lines inside fenced code blocks are skipped so a `# comment` in a shell
 * snippet is never mistaken for a heading (rehype-slug ignores them too).
 *
 * Lines inside a `<!-- tabs -->` block are skipped as well: those headings
 * become tab labels rather than headings in the rendered tree, so slugging them
 * here would knock the counter out of step with rehype-slug.
 */
export function extractToc(raw: string): TocEntry[] {
  const slugger = new GithubSlugger();
  const entries: TocEntry[] = [];
  let inFence = false;
  let inTabs = false;

  const lines = raw.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    if (/^\s*<!--\s*tabs\s*-->/.test(line)) {
      inTabs = true;
      continue;
    }
    if (/^\s*<!--\s*\/tabs\s*-->/.test(line)) {
      inTabs = false;
      continue;
    }
    if (inTabs) continue;

    const match = /^(#{1,6})\s+(.*)$/.exec(line);
    if (!match) continue;

    const depth = match[1].length;
    const text = stripInline(match[2]);
    const id = slugger.slug(text);

    if (depth === 2 || depth === 3) {
      entries.push({ depth, text, id, line: i });
    }
  }

  return entries;
}

/**
 * Group a flat ToC into `h2` sections with their `h3` children, in the shape
 * the shared {@link TableOfContents} renders (each section with children shows
 * as a collapsible group).
 */
export function groupToc(entries: TocEntry[]): TocSection[] {
  const sections: TocSection[] = [];
  for (const e of entries) {
    const node = { id: e.id, label: e.text };
    if (e.depth === 2 || sections.length === 0) {
      sections.push({ heading: node, children: [] });
    } else {
      sections[sections.length - 1].children.push(node);
    }
  }
  return sections;
}
